import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _typeof from "@babel/runtime/helpers/typeof";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import config from 'config';
import logger from '../logger';
var debug = logger.extend('errorLoggingExtension');
var reqPropertiesToInclude = ['headers', 'query', 'url', 'originalUrl'];

function reduceRequestProperties(itemsToKeep, item) {
  var _item = _slicedToArray(item, 2),
      key = _item[0],
      value = _item[1];

  if (reqPropertiesToInclude.includes(key)) {
    if (key === 'headers' && _typeof(value) === 'object') {
      Object.entries(value).forEach(function (headerItem) {
        var _headerItem = _slicedToArray(headerItem, 2),
            key = _headerItem[0],
            value = _headerItem[1];

        itemsToKeep["header_".concat(key)] = value;
      });
    } else {
      itemsToKeep[key] = JSON.stringify(value);
    }
  }

  return itemsToKeep;
}

export function createDefaultErrorHandler() {
  if (!process.browser) {
    // Can't create logger right away as it uses `config.get`, lazily create the
    // first time there's an error to report.
    var _logger;

    return function (err) {
      var additionalInfo = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

      if (config.get('server.graylog.enabled')) {
        if (!_logger) {
          var _require = require('./graylog'),
              makeGraylogLogger = _require.makeGraylogLogger;

          _logger = makeGraylogLogger();
        }

        var errPropertiesToRemove = ['stack'];
        var allowPropertyList = Object.getOwnPropertyNames(err).filter(function (property) {
          return !errPropertiesToRemove.includes(property);
        });

        var req = additionalInfo.req,
            rest = _objectWithoutProperties(additionalInfo, ["req"]);

        var requestProperties = req ? Object.entries(req).reduce(reduceRequestProperties, {}) : {};

        var errorToSend = _objectSpread(_objectSpread(_objectSpread({
          error_object: JSON.stringify(err, allowPropertyList),
          error_stack: err.stack
        }, err.customData), requestProperties), rest);

        _logger.error("".concat(err.name, " - ").concat(err.message), errorToSend);
      }
    };
  }
}
export default function errorLoggingExtension() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$onError = _ref.onError,
      onError = _ref$onError === void 0 ? createDefaultErrorHandler() : _ref$onError;

  return {
    id: 'errorLogging',
    server: process.browser ? {} : {
      configure: function configure(server) {
        // For error debugging purposes, add a middleware that will throw an
        // error if a `debugErrorType=middleware` query param is in the URL.
        function debugErrorMiddleware() {
          return function (req, res, next) {
            if (req.query.debugErrorType === 'middleware') {
              throw new Error('Debug error thrown via debugErrorType: middleware');
            }

            next();
          };
        }

        server.useTracked('debugErrorMiddleware', debugErrorMiddleware());
      },
      configureInitialMiddleware: function configureInitialMiddleware(server) {
        var isSentryEnabled = config.has('server.sentry.enabled') && config.get('server.sentry.enabled');
        var isSentrySharedSdk = config.has('server.sentry.useSharedSdk') && config.get('server.sentry.useSharedSdk');

        if (isSentryEnabled && isSentrySharedSdk) {
          var Sentry = require('@sentry/node');

          var sentrySampleRate = parseFloat(process.env.NEXT_PUBLIC_SENTRY_SAMPLE_RATE);
          var sentryTracesSampleRate = parseFloat(process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE);
          Sentry.init({
            dsn: config.get('server.sentry.dsn'),
            integrations: [// enable HTTP calls tracing
            new Sentry.Integrations.Http({
              tracing: true
            }), // enable Express.js middleware tracing
            new Sentry.Integrations.Express({
              server: server
            })],
            sampleRate: !isNaN(sentrySampleRate) ? sentrySampleRate : config.has('server.sentry.sampleRate') && config.get('server.sentry.sampleRate'),
            tracesSampleRate: !isNaN(sentryTracesSampleRate) ? sentryTracesSampleRate : config.get('server.sentry.tracesSampleRate'),
            environment: config.has('server.sentry.environment') && config.get('server.sentry.environment') || '',
            beforeSend: function beforeSend(event) {
              event.tags.storeId = config.has('public.brand.id') && config.get('public.brand.id');
              event.tags.storeLabel = config.has('public.brand.label') && config.get('public.brand.label');
              return event;
            }
          });
          server.useTracked('Sentry.Handlers.requestHandler', Sentry.Handlers.requestHandler());
          server.useTracked('Sentry.Handlers.tracingHandler', Sentry.Handlers.tracingHandler());
        }
      },
      configureErrorMiddleware: function configureErrorMiddleware(server) {
        var isSentryEnabled = config.has('server.sentry.enabled') && config.get('server.sentry.enabled');

        if (isSentryEnabled) {
          var Sentry = require('@sentry/node');

          server.useTracked('Sentry.Handlers.errorHandler', Sentry.Handlers.errorHandler());
        }

        function errorLoggingMiddleware() {
          return function (err, req, res, next) {
            debug('Calling onError for: %s', err);
            onError(err, {
              req: req
            });
            next(err);
          };
        }

        server.useTracked('errorLoggingMiddleware', errorLoggingMiddleware());
      }
    },
    app: process.browser ? undefined : {
      getInitialProps: function getInitialProps(_ref2) {
        var ctx = _ref2.ctx,
            Component = _ref2.Component;
        var debugErrorType;
        var isErrorPage = ctx.pathname === '/_error';

        if (isErrorPage) {
          if (ctx.err) {
            var pathname = ctx.pathname,
                query = ctx.query,
                req = ctx.req;
            debug('Calling onError for: %s', ctx.err);
            onError(ctx.err, {
              pathname: pathname,
              query: query,
              req: req
            });
          }
        } else {
          // This param is ignored if the error page is already being
          // rendered as we don't want to throw *another* error while
          // trying to render the error page.
          debugErrorType = ctx.query.debugErrorType || undefined;
        } // For error debugging purposes, throw an error if a
        // `debugErrorType=getInitialProps` query param is in the URL.


        if (debugErrorType === 'getInitialProps') {
          throw new Error('Debug error thrown via debugErrorType: getInitialProps');
        }

        return {
          debugErrorType: debugErrorType
        };
      },
      render: function render(_ref3, children) {
        var debugErrorType = _ref3.debugErrorType;

        // For error debugging purposes, throw an error during render if a
        // `debugErrorType=render` query param is in the URL.
        if (debugErrorType === 'render') {
          throw new Error('Debug error thrown via debugErrorType: render');
        }

        return children;
      }
    }
  };
}